import { useCallback, useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useWorkflows } from 'src/graphql/entities/workflow/hooks/use-workflows';
import type { WorkflowListItem } from 'src/graphql/entities/workflow/workflow.types';

interface UseWorkflowsWithPlatformResult {
  /** The partner's own workflows by name, then platform workflows by name. */
  workflows: WorkflowListItem[];
  loading: boolean;
  /** Resolves once both lists are back, so a caller can navigate to a row it just created. */
  refetchWorkflows: () => Promise<unknown>;
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

/**
 * The Settings > Workflows list. Platform (partner 0) workflows ride along for any other
 * partner so they can be read and copied; for partner 0 itself they are its own workflows.
 */
export function useWorkflowsWithPlatform(
  partnerId?: number | null,
): UseWorkflowsWithPlatformResult {
  const isPlatformPartner = partnerId === PLATFORM_PARTNER_ID;

  const own = useWorkflows(partnerId);
  // Skipped (nullish partner) for partner 0, whose own list already is the platform list.
  const platform = useWorkflows(isPlatformPartner ? null : PLATFORM_PARTNER_ID, {
    includePlatformLevelData: true,
  });

  const workflows = useMemo<WorkflowListItem[]>(
    () => [
      ...[...own.workflows]
        .sort(byName)
        .map((workflow) => ({ ...workflow, isPlatform: false })),
      ...(isPlatformPartner || partnerId === null || partnerId === undefined
        ? []
        : [...platform.workflows]
            .sort(byName)
            .map((workflow) => ({ ...workflow, isPlatform: true }))),
    ],
    [isPlatformPartner, own.workflows, partnerId, platform.workflows],
  );

  const { refetchWorkflows: refetchOwn } = own;
  const { refetchWorkflows: refetchPlatform } = platform;

  const refetchWorkflows = useCallback(
    () => Promise.all([refetchOwn(), isPlatformPartner ? undefined : refetchPlatform()]),
    [isPlatformPartner, refetchOwn, refetchPlatform],
  );

  return { workflows, loading: own.loading || platform.loading, refetchWorkflows };
}
